import { DataType, DateTime } from '@mode-switch/express';
import { Attr, Names } from '@mode-switch/model';
import {
  EncChannel,
  EncField,
  EncSort,
  Encoding,
  EncodingResult,
  MarkChannel,
  MarkType,
  SortType,
  VariableType,
} from '@mode-vegas/vegas';
import { isValid } from 'date-fns';

import { ChartTypes, FlamingoTypes } from '@mode/shared/contract-common';
import { formatInUTC } from '@mode/shared/util-js';
import { CompiledAttr, CompiledField, CompiledFormula, FormulaOperations, Switch } from '@mode/shared/util-switch';
import { TOTALS_TOKEN } from '..';
import * as ChartEncoding from './chart-encoding';
import * as EncodingHelpers from './chart-encoding-helpers';
import { ChartHierarchy } from './chart-hierarchy';
import { ChartSummaryEncoding } from './chart-summary-encoding';
import { SchemaModel, SwitchSchemaModel } from './schema-model';

/**
 * Updates an Encoding based on a drilldown operation. The drilldown operation
 * is dynamic, meaning that the user chooses the next field/level in the heirarchy
 * from any available field.
 *
 * In addition to the field changing, we also narrow the results into the specific
 * values that were selected. This includes all discrete fields on facets and mark
 * channels.
 *
 * The new Encoding will:
 *  - Add the drillToField on the x|y depending on the location of the target field
 *  - Add discrete filters for all of the discrete fields in the target.
 *
 * @param model ModeModel
 * @param view ModeView
 * @param encoding Original Encoding
 * @param target MarkTarget containing the columns and values that were clicked on
 * @param targetFieldId The id of the encoding field to drill on
 * @param drillToField The name of the new field to drill to
 * @param dateTruncUnit (Optional) Date unit if drill to field is a date
 * @returns Encoding updated with new field and filters
 */
export function updateEncodingForDynamicDrillDown(
  model: FlamingoTypes.ModeModel,
  view: FlamingoTypes.ModeView,
  encoding: Encoding,
  target: ChartTypes.MarkTarget,
  targetFieldId: string,
  drillToField: string,
  dateTruncUnit?: DateTime.Unit
) {
  // Get the target drill column information from the MarkTarget
  const targetColumn = target.columns?.find((column) => targetFieldId === column.id);
  if (targetColumn == null) {
    return encoding;
  }

  const drillOnEncField = EncodingHelpers.getEncFieldById(encoding, targetColumn.id ?? '');
  if (drillOnEncField == null || drillOnEncField.id == null) {
    return encoding;
  }

  // Find the new column to 'drill into' from the Attrs list by 'name'.
  const drillIntoAttr = Switch.Attrs.find(model, view.name, drillToField);
  if (drillIntoAttr == null) {
    return encoding;
  }

  // Determine the EncChannel of the field which is being drilled on. This is the actual current
  // position of the field in the encoding.
  const drillOnFieldEncChannel = findEncChannelForField(encoding, drillOnEncField.id);
  if (drillOnFieldEncChannel === undefined) {
    return encoding;
  }

  // Determine the destination channel of the target drill field. This is where the field
  // will move.
  const channel = findDestinationBasicChannel(encoding, drillOnFieldEncChannel);

  // We could be drilling on a continuous field (like DATETRUNC) into a discrete field.
  // Discrete fields are always placed before continuous, so this would result in the
  // 'drill into' field being placed on a facet rather than on the axis.
  //
  // In this situation, we are going to convert the fields on the axis to discrete
  // so that the existing fields will move the the facets and the new field (continuous or discrete)
  // will be placed on the axis. We do this before adding any filters so that the filter
  // type matches the field type.
  //
  // If there is no secondary axis, then the field is moved to the primary axis. In that
  // case we do not want to convert the primary axis to discrete since that will change
  // the encoding direction.

  const isMissingSecondary =
    (encoding.marks.channel === 'y' && [...encoding.x, ...encoding.column].length === 0) ||
    (encoding.marks.channel === 'x' && [...encoding.y, ...encoding.row].length === 0);

  const channelFields = channel === 'column' ? encoding.x : encoding.y;

  // Start updating the encoding.
  let encodingResult = EncodingHelpers.loadEncoding(encoding);

  if (!isMissingSecondary) {
    channelFields.forEach((encField) => {
      const compiled = CompiledField.fromEncField(view, encField);
      if (compiled.isContinuous && encField.id !== undefined) {
        encodingResult = EncodingHelpers.updateVegasFieldEncoding(view, encodingResult.encoding, {
          id: encField.id,
          variable: { type: VariableType.DISCRETE },
        });
      }
    });
  }

  // Create the filters for all discrete fields in the MarkTarget. The MarkTarget
  // includes all values for the data element that the user clicked on.
  const schemaModel = SwitchSchemaModel.make(model, view.name);
  target.columns?.forEach((targetColumn) => {
    const targetedField = EncodingHelpers.getEncFieldById(encodingResult.encoding, targetColumn.id ?? '');

    if (schemaModel !== undefined && targetedField != null && targetColumn.value !== TOTALS_TOKEN) {
      const modeField = schemaModel.makeField(targetedField);

      if (modeField?.role === Attr.Role.Dimension) {
        // Add discrete fields as filters. This include Measure Names which has
        // the effect of drilling into that particular measure.
        encodingResult = ChartSummaryEncoding.addVegasFilter(
          schemaModel,
          encodingResult.encoding,
          targetedField,
          targetColumn.value
        );
      }
    }
  });

  // Add the 'drill into' field to the same channel as the drill target field
  const drillIntoFormula: CompiledFormula = CompiledAttr.fromAttr(view, drillIntoAttr);
  const isTimestamp = drillIntoFormula.dataType === DataType.Name.Timestamp;

  // Special handling for tables. We want everything to be discrete so that the chart remains
  // a table and doesn't switch to another type when adding a DateTrunc.
  const isTextTable = encoding.marks.channel === '*' && encoding.marks.properties.type.value === MarkType.Text;

  // Add the drill into field to the end of the channel unless the last field on the channel
  // is Measure Names. Typically, the user wants Measure Names to stay in the same place. This is
  // particularly true of the Pivot Table QC. If Measure Names is the last field on the channel,
  // then add just before it.
  const currentAxisField = channel === 'column' ? encodingResult.encoding.x[0] : encodingResult.encoding.y[0];
  const at = currentAxisField?.formula === Names.Key ? currentAxisField.id : undefined;

  // We try to add dates as continuous except when:
  //  - it is a pivot table. Adding a continuous date would change it to a chart.
  //  - when adding before Measure Names since it has to be discrete to add before another discrete field.
  const zoneType =
    isTimestamp && !isTextTable && at === undefined
      ? EncodingHelpers.ZoneType.Continuous
      : EncodingHelpers.ZoneType.Discrete;

  let seriesFieldId: string | undefined;

  if (channel !== drillOnFieldEncChannel) {
    // Drilling on a MarkChannel
    // Move the drillOn field to the basic channel. Always add as discrete. Otherwise, the encoding
    // may switch to 'x|y' from '*' or vice-versa.
    const compiledDrillOnField = CompiledField.fromEncField(view, drillOnEncField);
    const result = EncodingHelpers.addOrMoveVegasFieldToBasicChannel(
      view,
      encodingResult.encoding,
      channel,
      compiledDrillOnField,
      undefined,
      undefined,
      EncodingHelpers.ZoneType.Discrete
    );
    if (result == null) {
      return encoding;
    }
    // Add drillInto field to the mark channel
    seriesFieldId = (() => {
      if (encodingResult.encoding.marks.channel === '*') {
        return '*';
      }
      const series = encodingResult.encoding.marks.series.find((s) => {
        return (
          s.properties.color.values.some((color) => color.id === drillOnEncField.id) ||
          s.properties.detail.values.some((detail) => detail.id === drillOnEncField.id) ||
          s.properties.size.values.some((size) => size.id === drillOnEncField.id)
        );
      });
      return series?.field;
    })();

    encodingResult = EncodingHelpers.addOrReplaceVegasField(
      result,
      drillIntoFormula,
      drillOnFieldEncChannel as MarkChannel,
      seriesFieldId,
      zoneType
    );
  } else {
    // Basic channel case.
    // Typically, we add the field to the end of the channel unless it is the Measure Names case.
    encodingResult = EncodingHelpers.addVegasField(encodingResult.encoding, drillIntoFormula, channel, zoneType, at);
  }

  if (isTimestamp) {
    // DateTime fields are added as continuous DateTrunc. The user can also specify a
    // DateUnit as part of the drill operation.
    // We need to modify the field we just added to use the proper DateUnit.

    // Find the field we added to the encoding. It is either the last field on the
    // channel, or the field before the 'at' field.
    const addedDrillField = findAddedDrillField(encodingResult.encoding, drillOnFieldEncChannel, seriesFieldId, at);

    if (!addedDrillField) {
      return encoding;
    }
    const newField = CompiledField.fromEncField(view, addedDrillField);
    const dateTruncField = newField.withNewFormula(
      FormulaOperations.dateTrunc(newField, dateTruncUnit ?? DateTime.Unit.Year),
      zoneType === EncodingHelpers.ZoneType.Discrete ? Attr.Type.Discrete : Attr.Type.Continuous
    );
    encodingResult = EncodingHelpers.updateVegasFieldEncoding(
      view,
      encodingResult.encoding,
      dateTruncField.toModifyField()
    );
  }
  // Update the automatic mark type if necessary
  encodingResult = EncodingHelpers.updateAutomaticMarkType(view, encoding, encodingResult);

  return encodingResult.encoding;
}

/**
 * Finds the EncChannel for the specified EncField id. This is specific to drilling
 * so it only looks in certain mark channels
 */
function findEncChannelForField(encoding: Encoding, encFieldId: string) {
  if ([...encoding.x, ...encoding.column].some((field) => field.id === encFieldId)) {
    return 'column';
  }
  if ([...encoding.y, ...encoding.row].some((field) => field.id === encFieldId)) {
    return 'row';
  }
  // Check for color, detail or size
  const markProperties =
    encoding.marks.channel === '*' ? [encoding.marks.properties] : encoding.marks.series.map((s) => s.properties);

  const colorFields = markProperties.flatMap((props) => props.color.values);
  if (colorFields.some((field) => field.id === encFieldId)) {
    return 'color';
  }
  const detailFields = markProperties.flatMap((props) => props.detail.values);
  if (detailFields.some((field) => field.id === encFieldId)) {
    return 'detail';
  }
  const sizeFields = markProperties.flatMap((props) => props.size.values);
  if (sizeFields.some((field) => field.id === encFieldId)) {
    return 'size';
  }
}

/**
 * Determines the basic channel the field that is being drill on should move to
 */
function findDestinationBasicChannel(encoding: Encoding, encChannel: EncChannel) {
  if (encChannel === 'row' || encChannel === 'column') {
    return encChannel;
  }
  // For mark channels, a '*' encoding goes to 'column'. For 'x|y' the field
  // goes to the primary axis channel.
  return encoding.marks.channel === 'y' ? 'row' : 'column';
}

/**
 * Finds the new field which was added to the encoding as a result of the drill down operation.
 */
function findAddedDrillField(
  encoding: Encoding,
  encChannel: EncChannel,
  seriesFieldId: string | undefined,
  at: string | undefined
) {
  let channelFields: Readonly<EncField>[] = [];
  const drillSeries = encoding.marks.series.find((s) => s.field === seriesFieldId);
  switch (encChannel) {
    case 'column':
      channelFields = [...encoding.column, ...encoding.x];
      break;
    case 'row':
      channelFields = [...encoding.row, ...encoding.y];
      break;
    case 'color':
    case 'detail':
    case 'size':
      channelFields =
        encoding.marks.channel === '*'
          ? [...encoding.marks.properties[encChannel].values]
          : [...(drillSeries?.properties[encChannel].values ?? [])];
      break;
  }
  return at === undefined
    ? channelFields[channelFields.length - 1]
    : channelFields[channelFields.findIndex((field) => field.id === at) - 1];
}

export function encode(model: SchemaModel, encoding: Encoding, target: ChartTypes.MarkTarget): EncodingResult {
  if (target.type === ChartTypes.TargetType.VegasChart) {
    // Find the field which is being drilled on.
    const encField = EncodingHelpers.getEncFieldById(encoding, target.drillTarget?.targetFieldId ?? '');
    if (encField == null) {
      return EncodingHelpers.loadEncoding(encoding);
    }
    // Modify the formula to be the next level
    const formula = ChartHierarchy.nextDateFormula(model, encField);

    // If the drill field is sorted, update the sort to use the new formula
    const existingSort = encoding.sorts.find(
      (encSort) => encSort.type !== SortType.MANUAL && encSort.source === encField.formula
    );
    encoding = updateDrillFieldSort(encoding, existingSort, formula);

    // Create filters for all dimension fields in the target columns
    target.columns?.forEach((column) => {
      const filterField = EncodingHelpers.getEncFieldById(encoding, column.id ?? '');
      const modeField = model.makeField(filterField);
      if (modeField?.role === Attr.Role.Dimension) {
        encoding = ChartSummaryEncoding.addVegasFilter(model, encoding, filterField, column.value).encoding;
      }
    });
    // Update the field formula after the filters are created
    encoding = EncodingHelpers.updateField(encoding, { id: encField.id ?? '', formula }).encoding;

    // If this is a multiple series chart, remove all series that we are not targetting.
    const targetSeries = encoding.marks.series.find((series) =>
      target.columns?.some((column) => column.id === series.field)
    );
    if (targetSeries != null) {
      // Remove the other series from the encoding.
      const otherSeriesIds = encoding.marks.series.reduce((others, series) => {
        if (series.field !== targetSeries.field) {
          others.push(series.field);
        }
        return others;
      }, [] as Array<string>);
      if (otherSeriesIds.length > 0) {
        encoding = EncodingHelpers.removeFieldsFromEncoding(encoding, otherSeriesIds).encoding;
      }
    }
    return EncodingHelpers.loadEncoding(encoding);
  } else {
    const vEncoding = EncodingHelpers.ensureVerticalEncoding(encoding);
    const swapped = vEncoding !== encoding;
    let result = encoding;

    switch (ChartEncoding.shape(vEncoding)) {
      case ChartEncoding.Shape.Grouped:
        result = grouped(model, vEncoding, target).encoding;
        break;
      case ChartEncoding.Shape.Combo:
        result = combo(model, vEncoding, target).encoding;
        break;
      case ChartEncoding.Shape.Stacked:
        result = stacked(model, vEncoding, target).encoding;
        break;
      case ChartEncoding.Shape.Radial:
        result = radial(model, vEncoding, target).encoding;
        break;
      default:
        return EncodingHelpers.loadEncoding(encoding);
    }
    return EncodingHelpers.loadEncoding(swapped ? EncodingHelpers.swapAxes(result) : result);
  }
}

function updateDrillFieldSort(encoding: Encoding, existingSort: EncSort | undefined, formula: string | undefined) {
  if (existingSort && formula) {
    // Remove the old sort
    encoding = EncodingHelpers.removeSort(encoding, existingSort.source).encoding;
    // Add the sort back with the new formula
    encoding = EncodingHelpers.addSort(encoding, { ...existingSort, source: formula }).encoding;
  }
  return encoding;
}

export function newChartType(type: ChartTypes.ChartType, target: ChartTypes.MarkTarget) {
  switch (type) {
    case 'bar':
      return 'stackedBar';
    case 'hBar':
      return 'hStackedBar';
    case 'linePlusBar':
      return target.y2 ? 'line' : 'stackedBar';
    default:
      return type;
  }
}

export function formatTargetValue(
  model: SchemaModel,
  encoding: Encoding,
  target: ChartTypes.MarkTarget
): string | undefined | null {
  encoding = EncodingHelpers.ensureVerticalEncoding(encoding);
  switch (ChartEncoding.shape(encoding)) {
    case ChartEncoding.Shape.Grouped:
      return formatGroupedValue(model, encoding, target);
    case ChartEncoding.Shape.Combo:
    case ChartEncoding.Shape.Stacked:
      return formatStackedValue(model, encoding, target);
    case ChartEncoding.Shape.Radial:
      return formatRadialSeriesValue(model, encoding, target);
  }
}

export function formatValue(
  model: SchemaModel,
  encoding: Encoding,
  encField: EncField,
  value: any
): string | undefined | null {
  encoding = EncodingHelpers.ensureVerticalEncoding(encoding); // should be unnecessary here, but let's be safe
  const field = model.compileEncField(encField);
  if (value == null) {
    return null;
  }

  if (field.hasDateFunc) {
    if (field.hasDatePart) {
      switch (field.dateFuncUnit) {
        case DateTime.Unit.Day:
          return `Day ${value.toString()}`;
        case DateTime.Unit.Hour:
          return `Hour ${value.toString()}`;
        case DateTime.Unit.Minute:
          return `Minute ${value.toString()}`;
        case DateTime.Unit.Second:
          return `Second ${value.toString()}`;
        default:
          return value.toString();
      }
    } else {
      if (!isValid(value)) {
        return null;
      }

      switch (field.dateFuncUnit) {
        case DateTime.Unit.Year:
          return formatInUTC(value, 'yyyy');
        case DateTime.Unit.Quarter:
        case DateTime.Unit.Month:
          return formatInUTC(value, 'yyyy-MM');
        case DateTime.Unit.Week:
          return `week of ${formatInUTC(value, 'yyyy-MM-dd')}`;
        case DateTime.Unit.Weekday:
        case DateTime.Unit.Day:
          return formatInUTC(value, 'yyyy-MM-dd');
        case DateTime.Unit.Hour:
        case DateTime.Unit.Minute:
        case DateTime.Unit.Second:
          return formatInUTC(value, 'yyyy-MM-dd HH:mm:ss');
      }
    }
  } else {
    return value;
  }
}

function stacked(model: SchemaModel, encoding: Encoding, target: ChartTypes.MarkTarget): EncodingResult {
  const { series, properties } = encoding.marks;
  const props = series.length ? series[0].properties : properties;

  const xEncField = encoding.x[0];
  const colorEncField = props.color.values[0];

  if (xEncField) {
    if (!target.x) {
      throw new Error('target.x is invalid');
    }
    if (!xEncField.id) {
      throw new Error('xEncField.id is invalid');
    }
    const { name, value } = target.x;
    const formula = ChartHierarchy.nextDateFormula(model, xEncField);
    // If the drill field is sorted, update the sort to use the new formula
    const existingSort = encoding.sorts.find(
      (encSort) => encSort.type !== SortType.MANUAL && encSort.source === xEncField.formula
    );
    encoding = updateDrillFieldSort(encoding, existingSort, formula);

    encoding = ChartSummaryEncoding.addNamesOrFilter(model, encoding, xEncField, name, value).encoding;
    encoding = EncodingHelpers.updateField(encoding, { id: xEncField.id, formula }).encoding;
  }

  if (colorEncField) {
    if (!target.series.color) {
      throw new Error('target.series.color is invalid');
    }
    const { name, value } = target.series.color;
    encoding = ChartSummaryEncoding.addNamesOrFilter(model, encoding, colorEncField, name, value).encoding;
  }

  return EncodingHelpers.loadEncoding(encoding);
}

function combo(model: SchemaModel, encoding: Encoding, target: ChartTypes.MarkTarget): EncodingResult {
  const xEncField = encoding.x[0];

  const existingSort = encoding.sorts.find(
    (encSort) => encSort.type !== SortType.MANUAL && encSort.source === xEncField?.formula
  );

  if (target.y2) {
    if (!encoding.y[0].id) {
      throw new Error('encoding.y[0].id is invalid');
    }
    encoding = EncodingHelpers.removeFieldFromEncoding(encoding, 'line', encoding.y[0].id).encoding;
    encoding = EncodingHelpers.convertEncoding('linePlusBar', 'line', encoding, model).encoding;
  } else {
    if (!encoding.y2[0].id) {
      throw new Error('encoding.y2[0].id is invalid');
    }
    encoding = EncodingHelpers.removeFieldFromEncoding(encoding, 'stackedBar', encoding.y2[0].id).encoding;
    encoding = EncodingHelpers.convertEncoding('linePlusBar', 'stackedBar', encoding, model).encoding;
  }

  if (xEncField) {
    if (!target.x) {
      throw new Error('target.x is invalid');
    }
    if (!xEncField.id) {
      throw new Error('xEncField.id is invalid');
    }
    const { name, value } = target.x;
    const formula = ChartHierarchy.nextDateFormula(model, xEncField);
    // If the drill field is sorted, update the sort to use the new formula
    encoding = updateDrillFieldSort(encoding, existingSort, formula);

    encoding = ChartSummaryEncoding.addNamesOrFilter(model, encoding, xEncField, name, value).encoding;
    encoding = EncodingHelpers.updateField(encoding, { id: xEncField.id, formula }).encoding;
  }

  return EncodingHelpers.loadEncoding(encoding);
}

function grouped(model: SchemaModel, encoding: Encoding, target: ChartTypes.MarkTarget): EncodingResult {
  const columnEncField = encoding.column[0];
  encoding = ChartSummaryEncoding.makeGrouped(model, encoding, target).encoding;

  if (columnEncField) {
    if (!columnEncField.id) {
      throw new Error('columnEncField.id is invalid');
    }
    const formula = ChartHierarchy.nextDateFormula(model, columnEncField);
    // If the drill field is sorted, update the sort to use the new formula
    const existingSort = encoding.sorts.find(
      (encSort) => encSort.type !== SortType.MANUAL && encSort.source === columnEncField.formula
    );
    encoding = updateDrillFieldSort(encoding, existingSort, formula);
    // Update the field to the new drill level
    encoding = EncodingHelpers.updateField(encoding, { id: columnEncField.id, formula }).encoding;

    if (!encoding.x[0].id) {
      throw new Error('encoding.x[0].id is invalid');
    }
    // Drop measure names from x to convert to stacked bar
    encoding = EncodingHelpers.removeFieldFromEncoding(encoding, 'stackedBar', encoding.x[0].id).encoding;
  }

  return EncodingHelpers.loadEncoding(encoding);
}

function radial(model: SchemaModel, encoding: Encoding, target: ChartTypes.MarkTarget): EncodingResult {
  encoding = ChartSummaryEncoding.makeRadial(model, encoding, target).encoding;

  const { series, properties } = encoding.marks;
  const props = series.length ? series[0].properties : properties;

  const colorEncField = props.color.values[0];

  if (colorEncField) {
    const formula = ChartHierarchy.nextDateFormula(model, colorEncField);
    if (!colorEncField.id) {
      throw new Error('colorEncField.id is invalid');
    }
    // If the drill field is sorted, update the sort to use the new formula
    const existingSort = encoding.sorts.find(
      (encSort) => encSort.type !== SortType.MANUAL && encSort.source === colorEncField.formula
    );
    encoding = updateDrillFieldSort(encoding, existingSort, formula);

    encoding = EncodingHelpers.updateField(encoding, { id: colorEncField.id, formula }).encoding;
  }

  return EncodingHelpers.loadEncoding(encoding);
}

function formatStackedValue(
  model: SchemaModel,
  encoding: Encoding,
  target: ChartTypes.MarkTarget
): string | undefined | null {
  return formatValue(model, encoding, encoding.x[0], target?.x?.value);
}

function formatGroupedValue(
  model: SchemaModel,
  encoding: Encoding,
  target: ChartTypes.MarkTarget
): string | undefined | null {
  if (!target.columns) {
    throw new Error('target.columns');
  }
  return formatValue(model, encoding, encoding.column[0], target.columns[0].value);
}

function formatRadialSeriesValue(
  model: SchemaModel,
  encoding: Encoding,
  target: ChartTypes.MarkTarget
): string | undefined | null {
  const { series, properties } = encoding.marks;
  const props = series.length ? series[0].properties : properties;
  return formatValue(model, encoding, props.color.values[0], target?.series?.color?.value);
}
